import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { getCaretCoordinates } from '../lib/textarea-caret';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useMemoEditor } from '../context/MemoEditorContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { format } from 'date-fns';

export function MemoEditor() {
    const { isOpen, mode, initialContent, memoId, memoDate, memoStatus, relatedMemoId, closeEditor } = useMemoEditor();
    const [content, setContent] = useState('');
    const [relatedMemo, setRelatedMemo] = useState<string | undefined>(undefined);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionType, setSuggestionType] = useState<'#' | '@' | null>(null);
    const [suggestionQuery, setSuggestionQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [date, setDate] = useState(new Date());
    const [status, setStatus] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');
    const [isStatusPickerOpen, setIsStatusPickerOpen] = useState(false);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [suggestionPos, setSuggestionPos] = useState({ top: 0, left: 0, placement: 'bottom' as 'bottom' | 'top' });

    const queryClient = useQueryClient();

    // Store latest props in ref to access inside useEffect without adding deps
    const propsRef = React.useRef({ initialContent, memoDate, memoStatus, relatedMemoId });
    useEffect(() => {
        propsRef.current = { initialContent, memoDate, memoStatus, relatedMemoId };
    });

    // Initialize content when editor opens
    useEffect(() => {
        if (isOpen) {
            const { initialContent, memoDate, memoStatus, relatedMemoId } = propsRef.current;
            setContent(initialContent);
            setDate(memoDate || new Date());
            setStatus(memoStatus || 'ACTIVE');
            setRelatedMemo(relatedMemoId);
            // Focus textarea after a short delay to allow render
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    // Set cursor to end
                    const len = initialContent.length;
                    textareaRef.current.setSelectionRange(len, len);
                }
            }, 50);
        }
    }, [isOpen]);

    const { data: tags } = useQuery({
        queryKey: ['tags'],
        queryFn: api.getTags,
        enabled: showSuggestions && suggestionType === '#',
    });

    const { data: entities } = useQuery({
        queryKey: ['entities'],
        queryFn: api.getEntities,
        enabled: showSuggestions && suggestionType === '@',
    });

    const createMutation = useMutation({
        mutationFn: api.createMemo,
        onMutate: async (newData: any) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['memos'] });
            await queryClient.cancelQueries({ queryKey: ['memo-stats'] });

            // Snapshot previous value
            const previousMemos = queryClient.getQueryData(['memos']);
            const previousStats = queryClient.getQueryData(['memo-stats']);

            // Optimistically update Memos list
            // We need to guess the structure, but usually simplest is enough for instant feedback
            const approximateMemo = {
                id: 'temp-' + Date.now(),
                ...newData,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            queryClient.setQueryData(['memos'], (old: any) => {
                if (!old?.data) return old;
                return {
                    ...old,
                    data: [approximateMemo, ...old.data]
                };
            });

            // Optimistically update Stats (Heatmap)
            // newData.memo_date is like "2023-10-27T12:00:00Z"
            const dateStr = newData.memo_date.split('T')[0];
            queryClient.setQueryData(['memo-stats'], (old: { data: { date: string; count: number }[] } | undefined) => {
                if (!old?.data) return old;
                const newStats = [...old.data];
                const existingStatIndex = newStats.findIndex(s => s.date === dateStr);

                if (existingStatIndex >= 0) {
                    newStats[existingStatIndex] = {
                        ...newStats[existingStatIndex],
                        count: newStats[existingStatIndex].count + 1
                    };
                } else {
                    newStats.push({ date: dateStr, count: 1 });
                }
                return { ...old, data: newStats };
            });

            closeEditor();

            return { previousMemos, previousStats };
        },
        onError: (err, _newTodo, context) => {
            queryClient.setQueryData(['memos'], context?.previousMemos);
            queryClient.setQueryData(['memo-stats'], context?.previousStats);
            // Re-open editor if it failed? Or show toast. For now just console error.
            console.error("Failed to create memo", err);
        },
        onSuccess: async () => {
            // Invalidate to get the real ID and server-side calculated fields
            await queryClient.invalidateQueries({ queryKey: ['memos'], refetchType: 'all' });
            await queryClient.invalidateQueries({ queryKey: ['memo-dates'], refetchType: 'all' });
            await queryClient.invalidateQueries({ queryKey: ['memo-stats'], refetchType: 'all' });
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.updateMemo(memoId!, data),
        onMutate: async (updatedData) => {
            // Cancel any outgoing refetches to avoid overwriting our optimistic update
            await queryClient.cancelQueries({ queryKey: ['memos'] });

            // Snapshot the previous value for rollback
            const queryCache = queryClient.getQueryCache();
            const memoQueries = queryCache.findAll({ queryKey: ['memos'] });
            const previousQueries = memoQueries.map(query => ({
                queryKey: query.queryKey,
                data: query.state.data
            }));

            // Optimistically update all memo caches
            memoQueries.forEach(query => {
                queryClient.setQueryData(query.queryKey, (old: any) => {
                    if (!old?.data) return old;

                    return {
                        ...old,
                        data: old.data.map((memo: any) =>
                            memo.memo_id === memoId
                                ? { ...memo, ...updatedData, updated_at: new Date().toISOString() }
                                : memo
                        )
                    };
                });
            });

            // Close editor immediately for instant feedback
            closeEditor();

            // Return context for potential rollback
            return { previousQueries };
        },
        onError: (err, _updatedData, context) => {
            // Rollback to previous state on error
            context?.previousQueries.forEach(({ queryKey, data }) => {
                queryClient.setQueryData(queryKey, data);
            });
            console.error('Failed to update memo:', err);
            // TODO: Show error toast notification
        },
        onSuccess: async () => {
            // Refetch to ensure consistency with server
            await queryClient.invalidateQueries({ queryKey: ['memos'], refetchType: 'all' });
            await queryClient.invalidateQueries({ queryKey: ['memo-dates'], refetchType: 'all' });
            await queryClient.invalidateQueries({ queryKey: ['memo-stats'], refetchType: 'all' });
        },
    });



    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        const pos = e.target.selectionStart;
        setContent(val);
        setCursorPosition(pos);

        // Check for trigger characters before cursor
        const textBeforeCursor = val.slice(0, pos);
        const lastHash = textBeforeCursor.lastIndexOf('#');
        const lastAt = textBeforeCursor.lastIndexOf('@');
        const lastSpace = textBeforeCursor.lastIndexOf(' ');
        const lastNewline = textBeforeCursor.lastIndexOf('\n');

        // Find the last word boundary (space, newline, or start of text)
        const lastBoundary = Math.max(lastSpace, lastNewline);

        let type: '#' | '@' | null = null;
        let query = '';

        if (lastHash > lastAt && lastHash > lastBoundary) {
            type = '#';
            query = textBeforeCursor.slice(lastHash + 1);
            // Validate that the query matches our pattern (word chars, Chinese, hyphens)
            // If there's a space or invalid character in the query, don't show suggestions
            if (/[^\w\u4e00-\u9fff.-]/.test(query)) {
                type = null;
            }
        } else if (lastAt > lastHash && lastAt > lastBoundary) {
            type = '@';
            query = textBeforeCursor.slice(lastAt + 1);
            // Validate that the query matches our pattern (word chars, Chinese, hyphens)
            // If there's a space or invalid character in the query, don't show suggestions
            if (/[^\w\u4e00-\u9fff.-]/.test(query)) {
                type = null;
            }
        }

        if (type) {
            setSuggestionType(type);
            setSuggestionQuery(query);
            setShowSuggestions(true);
            console.log('Suggestion triggered:', { type, query });

            if (textareaRef.current) {
                const { top, left, height } = getCaretCoordinates(textareaRef.current, pos);
                const rect = textareaRef.current.getBoundingClientRect();
                const visualTop = rect.top + top - textareaRef.current.scrollTop;
                const spaceBelow = window.innerHeight - visualTop;

                // If less than 300px below, flip to top
                const placement = spaceBelow < 300 ? 'top' : 'bottom';

                setSuggestionPos({
                    top: placement === 'bottom'
                        ? top - textareaRef.current.scrollTop + height
                        : top - textareaRef.current.scrollTop,
                    left: left - textareaRef.current.scrollLeft,
                    placement
                });
            }
        } else {
            setShowSuggestions(false);
            setSuggestionType(null);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
        if (overlayRef.current) {
            overlayRef.current.scrollTop = e.currentTarget.scrollTop;
            overlayRef.current.scrollLeft = e.currentTarget.scrollLeft;
        }
        if (showSuggestions) {
            setShowSuggestions(false);
        }
    };

    const insertItem = (item: string) => {
        // Get the actual current cursor position from the textarea
        const currentPos = textareaRef.current?.selectionStart ?? cursorPosition;

        const textBeforeCursor = content.slice(0, currentPos);
        const textAfterCursor = content.slice(currentPos);
        const lastTrigger = textBeforeCursor.lastIndexOf(suggestionType!);

        const newContent = textBeforeCursor.slice(0, lastTrigger + 1) + item + ' ' + textAfterCursor;
        setContent(newContent);
        setShowSuggestions(false);

        // Reset focus and cursor
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newPos = lastTrigger + 1 + item.length + 1;
                textareaRef.current.setSelectionRange(newPos, newPos);
                setCursorPosition(newPos); // Update the state to keep it in sync
            }
        }, 0);
    };

    const suggestions = suggestionType === '#' ? tags?.data : entities?.data;
    const filteredSuggestions = suggestions?.filter((item: any) => {
        // Use toLowerCase for case-insensitive matching, but preserve the original casing (uppercase for tags) for insertion
        const val = typeof item === 'string' ? item : (item?.code || item?.name || item?.title || item?.label || item?.id || '');
        return val.toLowerCase().includes(suggestionQuery.toLowerCase());
    });

    // Reset selection when suggestions change
    useEffect(() => {
        setSelectedIndex(0);
    }, [suggestionQuery, suggestionType, showSuggestions]);

    const handleSave = () => {
        if (!content.trim()) return;

        const memoData: any = {
            content,
            // Normalize date to Noon UTC to ensure it stays on the selected day regardless of timezone
            // This prevents "off-by-one day" errors when converting 00:00 Local to UTC on the backend
            memo_date: `${format(date, 'yyyy-MM-dd')}T12:00:00Z`,
            status,
            // Always include related_memos as array (empty if none)
            related_memos: relatedMemo ? [relatedMemo] : []
        };

        if (mode === 'edit' && memoId) {
            updateMutation.mutate(memoData);
        } else {
            createMutation.mutate(memoData);
        }
    };



    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (showSuggestions && filteredSuggestions && filteredSuggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((prev) => (prev + 1) % filteredSuggestions.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((prev) => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length);
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const firstItem = filteredSuggestions[selectedIndex];
                const val = typeof firstItem === 'string' ? firstItem : (firstItem?.code || firstItem?.name || firstItem?.title || firstItem?.label || firstItem?.id || 'Unknown');
                insertItem(val);
                return;
            }
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSave();
        }

        if (e.key === 'Escape') {
            if (showSuggestions) {
                setShowSuggestions(false);
                e.stopPropagation(); // Prevent closing modal
            } else {
                closeEditor();
            }
        }
    };

    const insertTrigger = (trigger: '#' | '@') => {
        // Get the actual current cursor position from the textarea
        const currentPos = textareaRef.current?.selectionStart ?? cursorPosition;

        const textBeforeCursor = content.slice(0, currentPos);
        const textAfterCursor = content.slice(currentPos);
        const newContent = textBeforeCursor + trigger + textAfterCursor;
        setContent(newContent);

        // Update state to trigger suggestions immediately
        setSuggestionType(trigger);
        setSuggestionQuery('');
        setShowSuggestions(true);

        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newPos = currentPos + 1;
                textareaRef.current.setSelectionRange(newPos, newPos);
                setCursorPosition(newPos);

                const { top, left, height } = getCaretCoordinates(textareaRef.current, newPos);
                const rect = textareaRef.current.getBoundingClientRect();
                const visualTop = rect.top + top - textareaRef.current.scrollTop;
                const spaceBelow = window.innerHeight - visualTop;

                const placement = spaceBelow < 300 ? 'top' : 'bottom';

                setSuggestionPos({
                    top: placement === 'bottom'
                        ? top - textareaRef.current.scrollTop + height
                        : top - textareaRef.current.scrollTop,
                    left: left - textareaRef.current.scrollLeft,
                    placement
                });
            }
        }, 0);
    };

    if (!isOpen) return null;

    const highlightContent = (text: string) => {
        // Enhanced regex to match #, @, or $ followed by:
        // - Word characters (\w includes a-z, A-Z, 0-9, _)
        // - Chinese characters (expanded Unicode range: \u4e00-\u9fff)
        // - Hyphens (-)
        // The pattern continues matching as long as these characters appear
        const parts = text.split(/([#@$][\w\u4e00-\u9fff.-]+)/g);
        return parts.map((part, i) => {
            if (part.startsWith('#')) {
                return <span key={i} className="text-blue-500">{part}</span>;
            }
            if (part.startsWith('@')) {
                return <span key={i} className="text-amber-500">{part}</span>;
            }
            if (part.startsWith('$')) {
                return <span key={i} className="text-purple-500">{part}</span>;
            }
            return <span key={i}>{part}</span>;
        });
    };

    const isPending = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={closeEditor}
            />

            {/* Editor Sheet */}
            <div className="relative w-full h-[50vh] bg-white dark:bg-zinc-950 shadow-2xl border-t border-zinc-200 dark:border-zinc-800 animate-in slide-in-from-bottom duration-300 flex flex-col rounded-t-xl overflow-hidden pb-safe">
                {/* Drag Handle */}
                <div className="flex justify-center pt-2 pb-1 shrink-0" onClick={() => { }}>
                    <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
                </div>

                {/* Close button in top-right */}
                <button
                    onClick={closeEditor}
                    className="absolute top-2 right-2 z-30 p-1.5 text-zinc-400 hover:text-zinc-600 dark:text-zinc-600 dark:hover:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                    title="Close"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
                <div className="flex-1 w-full max-w-3xl mx-auto relative flex flex-col">
                    <div className="relative flex-1 w-full">
                        {/* Highlighter Overlay */}
                        <div
                            ref={overlayRef}
                            className="absolute inset-0 p-8 text-base leading-relaxed whitespace-pre-wrap break-words pointer-events-none text-transparent dark:text-transparent overflow-hidden"
                            aria-hidden="true"
                        >
                            <div className="text-zinc-900 dark:text-zinc-100">
                                {highlightContent(content)}
                                {/* Add a zero-width space to ensure height matches if ending with newline */}
                                {content.endsWith('\n') && <br />}
                            </div>
                        </div>

                        {/* Actual Textarea */}
                        <textarea
                            ref={textareaRef}
                            autoFocus
                            className="absolute inset-0 w-full h-full p-8 text-base leading-relaxed bg-transparent border-none focus:ring-0 resize-none placeholder:text-zinc-300 dark:placeholder:text-zinc-700 outline-none text-transparent caret-zinc-900 dark:caret-zinc-100 selection:bg-blue-200/50 selection:text-zinc-900 dark:selection:bg-blue-800/50 dark:selection:text-zinc-100 overflow-y-auto"
                            placeholder="What's on your mind?"
                            value={content}
                            onChange={handleInput}
                            onScroll={handleScroll}
                            onClick={(e) => setCursorPosition(e.currentTarget.selectionStart)}
                            onKeyUp={(e) => setCursorPosition(e.currentTarget.selectionStart)}
                            onKeyDown={handleKeyDown}
                            spellCheck={false}
                        />
                    </div>

                    {showSuggestions && filteredSuggestions && filteredSuggestions.length > 0 && (
                        <div
                            className={`absolute w-64 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-xl max-h-64 overflow-y-auto z-50 ${suggestionPos.placement === 'top' ? '-translate-y-full mb-1' : 'mt-1'}`}
                            style={{ top: suggestionPos.top, left: suggestionPos.left }}
                        >
                            {filteredSuggestions.map((item: any, index: number) => {
                                console.log('Rendering item:', item);
                                const val = typeof item === 'string' ? item : (item?.code || item?.name || item?.title || item?.label || item?.id || 'Unknown');
                                const isSelected = index === selectedIndex;
                                return (
                                    <button
                                        key={index}
                                        className={`w-full text-left px-4 py-3 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm transition-colors border-b border-zinc-50 dark:border-zinc-800/50 last:border-0 ${isSelected ? 'bg-zinc-100 dark:bg-zinc-800' : 'bg-zinc-50 dark:bg-zinc-900'}`}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => insertItem(val)}
                                    >
                                        <span className={`opacity-70 mr-1 font-medium ${suggestionType === '#' ? 'text-blue-500' : 'text-amber-500'}`}>{suggestionType}</span>
                                        <span className={suggestionType === '#' ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}>{val}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div className="p-4 pb-8 flex flex-col gap-3 bg-white dark:bg-zinc-950 border-t border-zinc-100 dark:border-zinc-900 z-20">
                        {/* First row: related memo label (if present) */}
                        {relatedMemo && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg w-fit">
                                <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">Related: {relatedMemo}</span>
                                <button
                                    onClick={() => setRelatedMemo(undefined)}
                                    className="text-purple-400 hover:text-purple-600 dark:text-purple-500 dark:hover:text-purple-300"
                                    title="Remove related memo"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* Second row: toolbar and save button */}
                        <div className="flex items-center justify-between">
                            <div className="flex gap-1 items-center">
                                <button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => insertTrigger('#')}
                                    className="p-3 text-zinc-400 hover:text-blue-500 dark:text-zinc-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                    title="Add tag"
                                >
                                    <span className="font-bold text-xl">#</span>
                                </button>
                                <button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => insertTrigger('@')}
                                    className="p-3 text-zinc-400 hover:text-amber-500 dark:text-zinc-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                                    title="Mention entity"
                                >
                                    <span className="font-bold text-xl">@</span>
                                </button>

                                <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-800 mx-1" />

                                {/* Status selector */}
                                <div className="relative">
                                    <button
                                        onClick={() => setIsStatusPickerOpen(!isStatusPickerOpen)}
                                        className="flex items-center gap-2 px-3 py-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors"
                                        title={`Status: ${status}`}
                                    >
                                        <div className={`w-2 h-2 rounded-full ${status === 'ACTIVE' ? 'bg-green-500' : 'bg-zinc-400'}`} />
                                    </button>

                                    {isStatusPickerOpen && (
                                        <>
                                            <div className="fixed inset-0 z-30" onClick={() => setIsStatusPickerOpen(false)} />
                                            <div className="absolute bottom-full left-0 mb-2 w-48 bg-white dark:bg-zinc-900 rounded-lg shadow-xl border border-zinc-200 dark:border-zinc-800 py-1 z-40 flex flex-col [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                                                {['ACTIVE', 'ARCHIVED'].map((s) => (
                                                    <button
                                                        key={s}
                                                        onClick={() => {
                                                            setStatus(s as 'ACTIVE' | 'ARCHIVED');
                                                            setIsStatusPickerOpen(false);
                                                        }}
                                                        className={`flex items-center gap-2 px-4 py-2 text-left text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors uppercase ${status === s ? 'text-zinc-900 dark:text-zinc-100 bg-zinc-50 dark:bg-zinc-800/50' : 'text-zinc-500 dark:text-zinc-400'}`}
                                                    >
                                                        <div className={`w-2 h-2 rounded-full ${s === 'ACTIVE' ? 'bg-green-500' : 'bg-zinc-400'}`} />
                                                        {s}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Date selector */}
                                <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            onMouseDown={(e) => e.preventDefault()}
                                            className="flex items-center gap-2 px-3 py-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors"
                                            title="Change date"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                            <span className="text-sm font-medium">
                                                {format(date, "MMM d, yyyy")}
                                            </span>
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0 z-[110]" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={date}
                                            onSelect={(newDate) => {
                                                if (newDate) {
                                                    setDate(newDate);
                                                    setIsDatePickerOpen(false);
                                                }
                                            }}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <button
                                disabled={!content.trim() || isPending}
                                onClick={handleSave}
                                className="px-6 py-2.5 text-sm font-medium text-white bg-zinc-900 rounded-full hover:bg-zinc-800 disabled:opacity-50 dark:bg-zinc-50 dark:text-zinc-900 transition-all hover:shadow-lg active:scale-95"
                            >
                                {isPending ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
